import {
  Card,
  CrudMode,
  DataDisplay,
  formatPhoneNumber,
  Grid,
  GridBreak,
  pn,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { AddressFields, Input, Select } from "components";
import { Address, SubPartner, SubPartnerViewResource } from "generated/openApi";

import produce from "immer";
import "moment-timezone";
import ApiService from "services/apiService";

export const SubPartnerPageBody = (props: {
  crudMode: CrudMode;
  subPartner: SubPartner;
  setSubPartner: (value: SubPartner) => void;
  viewResource: SubPartnerViewResource;
  validationErrors: ValidationErrors;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, fullFieldBreakpoints } = useElementBreakpoints();
  const properties = props.viewResource.properties || [];

  const nameField = () => {
    const propertyName = pn<SubPartner>("name");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.subPartner.name}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.subPartner.name}
        onChange={(e) =>
          props.setSubPartner(
            produce(props.subPartner, (draft) => {
              draft.name = e.target.value;
            })
          )
        }
      />
    );
  };

  const contactContributorField = () => {
    const propertyName = pn<SubPartner>("contactContributorId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        {props.subPartner.contactContributor && (
          <>
            {props.subPartner.contactContributor?.lastName}, {props.subPartner.contactContributor?.firstName}
          </>
        )}
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.subPartner.contactContributor}
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        onChange={(e, value) =>
          props.setSubPartner(
            produce(props.subPartner, (draft) => {
              draft.contactContributorId = value?.id;
              draft.contactContributor = value;
            })
          )
        }
        fetchFunction={(searchString: string) => ApiService.search.contributors(searchString)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const phoneField = () => {
    const propertyName = pn<SubPartner>("phone");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatPhoneNumber(props.subPartner.phone)}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.subPartner.phone}
        onChange={(e) =>
          props.setSubPartner(
            produce(props.subPartner, (draft) => {
              draft.phone = e.target.value;
            })
          )
        }
      />
    );
  };

  const faxField = () => {
    const propertyName = pn<SubPartner>("fax");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatPhoneNumber(props.subPartner.fax)}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.subPartner.fax}
        onChange={(e) =>
          props.setSubPartner(
            produce(props.subPartner, (draft) => {
              draft.fax = e.target.value;
            })
          )
        }
      />
    );
  };

  return (
    <Grid ref={breakpointsRef} container spacing="sm">
      <Grid item xs={smallFieldBreakpoints}>
        {nameField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {contactContributorField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {phoneField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {faxField()}
      </Grid>

      <GridBreak />

      <Grid item xs={fullFieldBreakpoints}>
        <AddressFields
          crudMode={props.crudMode}
          address={props.subPartner.address || ({} as Address)}
          setAddress={(value: Address) =>
            props.setSubPartner(
              produce(props.subPartner, (draft) => {
                draft.address = value;
              })
            )
          }
          properties={properties.find((x) => x.propertyName === pn<SubPartner>("address"))?.children || []}
          validationErrors={props.validationErrors[pn<SubPartner>("address")] || []}
        />
      </Grid>
    </Grid>
  );
};
